import React, { useState, useEffect } from 'react'
import {ChevronLeft, ChevronRight} from 'lucide-react'

interface HeroQuizSliderProps {
  images: Array<{ imageUrl: string; altText: string; _id: string; caption?: string }>
}

export function HeroQuizSlider({ images }: HeroQuizSliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (images.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [images.length])

  if (images.length === 0) return null

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  return (
    <div className="relative w-full group">
      {/* Image Container - Pleine largeur, hauteur fixe, zoom si nécessaire */}
      <div className="relative w-full h-[350px] md:h-[500px] lg:h-[600px] overflow-hidden">
        {images.map((img, index) => (
          <div
            key={img._id}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img.imageUrl}
              alt={img.altText}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
      </div>

      {/* Caption Text */}
      {images[currentIndex]?.caption && (
        <div className="mt-4 text-center px-4">
          <p className="text-lg md:text-xl text-gray-700 font-medium">
            {images[currentIndex].caption}
          </p>
        </div>
      )}

      {/* Navigation Arrows - Only show if multiple images */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-[175px] md:top-[250px] lg:top-[300px] -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
            aria-label="Image précédente"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-4 top-[175px] md:top-[250px] lg:top-[300px] -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
            aria-label="Image suivante"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-[calc(100%-350px+16px)] md:bottom-[calc(100%-500px+16px)] lg:bottom-[calc(100%-600px+16px)] left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all shadow ${
                  index === currentIndex
                    ? 'bg-teal-600 w-8'
                    : 'bg-white/80 hover:bg-white'
                }`}
                aria-label={`Aller à l'image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
